// build: layers — the stage board. Rows are the layers the learner has
// actually earned (their keepers decide: drums when beat/pulse exists, chords
// when chordLoop, melody when trackMelody/homeCall, bass when bassline, and
// the drone always); columns are config.stages (e.g. ['A','B'] or
// ['sparse','building','full','drop']). Every cell toggles that layer in that
// stage, and ▶ walks the stages in order via playTrackMix — config.stageBars
// bars each (default 2), approximated with timers off each mix's cycleMs.
//
// Soft checks (moment.checks) run against { stages } with the usual "keep it
// anyway" escape. Saving calls onKeeper(keeperId, keeperName, { stages })
// where stages is [{ id, layers: { drums: true, ... } }].

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { runChecks } from "../../../lib/homeKeyTheory";
import { resumeAudio } from "../../../lib/homeKeyAudio";
import { playTrackMix } from "../../../lib/homeKeyGroove";

const LAYER_ORDER = ["drums", "bass", "chords", "melody", "drone"];
const LAYER_LABELS = {
  drums: "drums",
  bass: "bass",
  chords: "chords",
  melody: "melody",
  drone: "drone",
};

const availableLayers = (keepers) => ({
  drums: Boolean(keepers.beat || keepers.pulse),
  bass: Boolean(keepers.bassline),
  chords: Boolean(keepers.chordLoop),
  melody: Boolean(keepers.trackMelody || keepers.homeCall),
  drone: true,
});

export default function LayerBoard({ moment, onReadyChange, onKeeper, keepers }) {
  const config = moment.config || {};
  const stageIds = useMemo(() => config.stages || ["A", "B"], [config.stages]);
  const stageBars = config.stageBars || 2;

  const rows = useMemo(() => {
    const have = availableLayers(keepers || {});
    return (config.layers || LAYER_ORDER).filter((id) => have[id]);
  }, [config.layers, keepers]);

  // Every cell starts on — the learner carves space out of the full mix.
  const [stages, setStages] = useState(() =>
    stageIds.map((id) => ({
      id,
      layers: Object.fromEntries(rows.map((row) => [row, true])),
    }))
  );
  const [playingStage, setPlayingStage] = useState(-1);
  const [saved, setSaved] = useState(false);

  const playRef = useRef(null);
  const timersRef = useRef([]);
  const stagesRef = useRef(stages);
  stagesRef.current = stages;

  const stopPlayback = useCallback(() => {
    if (playRef.current && playRef.current.stop) playRef.current.stop();
    playRef.current = null;
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
    setPlayingStage(-1);
  }, []);
  useEffect(() => () => stopPlayback(), [stopPlayback]);

  const ready = saved;
  useEffect(() => {
    if (onReadyChange) onReadyChange(ready);
  }, [ready, onReadyChange]);

  const results = useMemo(
    () => runChecks(moment.checks || [], { stages }),
    [moment.checks, stages]
  );
  const allPass = results.every((r) => r.pass);

  const toggleCell = (stageIndex, row) => {
    if (saved) return;
    setStages((prev) =>
      prev.map((stage, i) =>
        i === stageIndex
          ? { ...stage, layers: { ...stage.layers, [row]: !stage.layers[row] } }
          : stage
      )
    );
  };

  const lensId =
    (keepers && keepers.trackStyle && keepers.trackStyle.lensId) || "dreamPop";

  const playAll = () => {
    if (playRef.current || timersRef.current.length) {
      stopPlayback();
      return;
    }
    resumeAudio();
    const startStage = (i) => {
      const list = stagesRef.current;
      if (i >= list.length) {
        stopPlayback();
        return;
      }
      if (playRef.current && playRef.current.stop) playRef.current.stop();
      const handle = playTrackMix({
        keepers: keepers || {},
        lensId,
        layers: { ...list[i].layers },
      });
      playRef.current = handle;
      setPlayingStage(i);
      const cycleMs = (handle && handle.cycleMs) || 4000;
      const stageMs = Math.max(400, (cycleMs * stageBars) / 4);
      timersRef.current.push(setTimeout(() => startStage(i + 1), stageMs));
    };
    startStage(0);
  };

  const save = () => {
    stopPlayback();
    onKeeper(moment.keeperId, moment.keeperName, {
      stages: stages.map((s) => ({ id: s.id, layers: { ...s.layers } })),
    });
    setSaved(true);
  };

  const playing = playingStage >= 0;

  return (
    <div className="hk-moment-body">
      <p>
        {config.prompt ||
          "Each column is a stretch of your track; each row is a layer you made. Toggle cells to decide who plays when, then press play and hear the shape."}
      </p>

      <div
        className="hk-layer-board"
        role="group"
        aria-label="layers by stage"
        style={{ gridTemplateColumns: `auto repeat(${stages.length}, minmax(3.2rem, 1fr))` }}
      >
        <span className="hk-layer-corner" aria-hidden="true" />
        {stages.map((stage, i) => (
          <span
            key={stage.id}
            className={`hk-layer-head${playingStage === i ? " hk-layer-head--now" : ""}`}
          >
            {stage.id}
          </span>
        ))}
        {rows.map((row) => (
          <React.Fragment key={row}>
            <span className="hk-layer-row-label">{LAYER_LABELS[row] || row}</span>
            {stages.map((stage, i) => (
              <button
                key={stage.id}
                type="button"
                className={`hk-layer-cell${stage.layers[row] ? " hk-layer-cell--on" : ""}${
                  playingStage === i ? " hk-layer-cell--now" : ""
                }`}
                aria-pressed={Boolean(stage.layers[row])}
                aria-label={`${LAYER_LABELS[row] || row} in stage ${stage.id}: ${
                  stage.layers[row] ? "playing" : "resting"
                }`}
                onClick={() => toggleCell(i, row)}
                disabled={saved}
              />
            ))}
          </React.Fragment>
        ))}
      </div>

      <div className="hk-choice-row">
        <button
          type="button"
          className={`hk-demo-btn${playing ? " hk-demo-btn--on" : ""}`}
          onClick={playAll}
        >
          {playing ? "■ stop" : "▶ play the stages"}
        </button>
      </div>

      {!saved && results.length > 0 && (
        <div className="hk-checks">
          {results.map((r) => (
            <div
              key={r.id}
              className={`hk-check-note${r.pass ? " hk-check-note--pass" : ""}`}
            >
              {r.pass ? "✓ " : "~ "}
              {r.message}
            </div>
          ))}
        </div>
      )}

      {!saved && (
        <div className="hk-choice-row">
          <button
            type="button"
            className="hk-btn hk-btn--primary"
            disabled={!allPass}
            onClick={save}
          >
            save keeper
          </button>
          {!allPass && (
            <button type="button" className="hk-btn hk-btn--ghost" onClick={save}>
              actually, I like it — keep it anyway
            </button>
          )}
        </div>
      )}

      {saved && (
        <p className="hk-echo-status">
          saved to your track: <strong>{moment.keeperName}</strong>. It's part of
          the music now.
        </p>
      )}
    </div>
  );
}
